import Phaser from "phaser";
import { ItemDefinition, TILE_SIZE } from "../../../../models/common";
import { COLORS } from "../../../../ui/colors";
import { SpriteLoader } from "../../../../sprite-loader";
import { ItemSlot } from "../../../../ui/item-slot";
import { drawWidgetBackground, QUEST_WIDGET_W } from "./widget-background";

/**
 * CraftWidget: Shows the station with the most upgrade materials and the required items.
 * Usage: new CraftWidget(scene, x, y, station, materials, playerMaterialCounts)
 */
export class CraftWidget extends Phaser.GameObjects.Container {
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    stationIcon: { spriteSheet: string; spriteX: number; spriteY: number },
    materials: { def: ItemDefinition; required: number; owned: number }[],
    currentLevel: number
  ) {
    super(scene, x, y);

    // Left padding for all contents
    const LEFT_PADDING = 60;
    const STATION_SIZE = TILE_SIZE * 1.5;
    const slotSpacing = 52;
    const slotSize = 40;
    const matStartX = STATION_SIZE / 2 + slotSpacing / 2;

    // Label (created before bg so we have label.height for bg dimensions)
    const nextLevel = currentLevel + 1;
    const label = scene.add.text(32, -14, `Upgrade to lvl ${nextLevel}`, {
      fontSize: "14px",
      color: COLORS.YELLOW,
      fontFamily: "Anton",
      fontStyle: "bold",
      align: "left",
    });
    label.setOrigin(0, 0);

    // Background
    const rowY = label.height + 8;
    const bgX = 8;
    const bgY = -(14 + 6);
    const bgW = QUEST_WIDGET_W;
    const bgH = Math.abs(bgY) + rowY + STATION_SIZE / 2 + 16;
    const bg = scene.add.graphics();
    drawWidgetBackground(bg, bgX, bgY, bgW, bgH);
    this.add(bg);
    this.add(label);

    // Row container for station + materials
    const row = scene.add.container(LEFT_PADDING, rowY);

    // Station icon (twice as big)
    const stationSprite = SpriteLoader.createSprite(
      scene,
      stationIcon.spriteSheet,
      stationIcon.spriteX,
      stationIcon.spriteY,
      0,
      0,
      true,
    );
    stationSprite.displayWidth = STATION_SIZE;
    stationSprite.displayHeight = STATION_SIZE;
    stationSprite.setOrigin(0.5);
    row.add(stationSprite);

    // Materials in slots, spaced to right of station icon
    materials.forEach((mat, i) => {
      const matX = matStartX + i * slotSpacing;
      // Create slot
      const slot = new ItemSlot(matX - slotSize / 2, -slotSize / 2, true);
      const slotGraphic = slot.createRectangle(scene);
      row.add(slotGraphic);
      // Material icon centered in slot
      const matSprite = SpriteLoader.createSprite(
        scene,
        mat.def.spriteSheet,
        mat.def.spriteX,
        mat.def.spriteY,
        matX,
        0,
        true,
      );
      matSprite.displayWidth = TILE_SIZE;
      matSprite.displayHeight = TILE_SIZE;
      matSprite.setOrigin(0.5);
      if (mat.owned < mat.required) matSprite.setTint(0xff6666);
      row.add(matSprite);
      // Quantity text
      const qtyText = scene.add.text(
        matX + slotSize / 2 - 5,
        -14,
        `x${mat.required}`,
        {
          fontSize: "12px",
          color: mat.owned >= mat.required ? COLORS.WHITE : COLORS.ORANGE,
          fontFamily: "Anton",
          fontStyle: "bold",
        },
      );
      qtyText.setOrigin(1, 0.5);
      row.add(qtyText);
      // Owned text
      const ownedText = scene.add.text(
        matX + slotSize / 2 - 5,
        14,
        `(${mat.owned})`,
        {
          fontSize: "12px",
          color: mat.owned >= mat.required ? COLORS.GREEN : COLORS.ORANGE,
          fontFamily: "Anton",
        },
      );
      ownedText.setOrigin(1, 0.5);
      row.add(ownedText);
    });

    this.add(row);
  }
}
